import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Building2, Mail, Lock, AlertCircle } from "lucide-react";

import { API_URL } from "../lib/utils";
const DEFAULT_LOGIN_BG = "https://images.unsplash.com/photo-1766157669263-399796aa3ccd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc1NTc3NzMxfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user, error: authError } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [loginBg, setLoginBg] = useState(DEFAULT_LOGIN_BG);
  const [appName, setAppName] = useState("Gestionale HR");
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoSize, setLogoSize] = useState(48);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${API_URL}${url}`;
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/settings/public`)
      .then(res => {
        if (res.data.login_image_url) setLoginBg(resolveUrl(res.data.login_image_url));
        if (res.data.app_name) setAppName(res.data.app_name);
        if (res.data.logo_url) setLogoUrl(resolveUrl(res.data.logo_url));
        if (res.data.logo_size) setLogoSize(res.data.logo_size);
        // Apply primary color
        if (res.data.primary_color) {
          document.documentElement.style.setProperty("--primary-color", res.data.primary_color);
        }
        // Set favicon from logo
        if (res.data.logo_url) {
          const faviconUrl = res.data.logo_url.startsWith("http") ? res.data.logo_url : `${API_URL}${res.data.logo_url}`;
          let link = document.querySelector("link[rel~='icon']");
          if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
          link.href = faviconUrl;
        }
      })
      .catch(() => {})
      .finally(() => setSettingsLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      const target = user?.role === "admin" ? "/dashboard" : "/schedules";
      navigate(target);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const loggedUser = await login(formData.email, formData.password);
      const target = loggedUser?.role === "admin" ? "/dashboard" : "/schedules";
      navigate(target);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!settingsLoaded) {
    return <div className="min-h-screen bg-[#F8F9FA]" />;
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${loginBg})` }}
        data-testid="login-background-image"
      >
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-bold font-outfit tracking-tight mb-4">
            {appName}
          </h1>
          <p className="text-lg text-white/80 font-ibm-plex">
            Gestisci dipendenti, turni e stipendi in modo semplice e veloce.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" style={{ height: logoSize }} className="object-contain" data-testid="login-logo" />
              ) : (
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--primary-color, #09090B)" }}>
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              )}
            </div>
            <CardTitle className="text-2xl font-outfit font-bold">Accedi</CardTitle>
            <CardDescription className="font-ibm-plex">
              Inserisci le tue credenziali per continuare
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-ibm-plex">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="nome@azienda.it"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 font-ibm-plex"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-ibm-plex">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 font-ibm-plex"
                    required
                  />
                </div>
              </div>

              {(error || authError) && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-md text-sm font-ibm-plex">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error || authError}</span>
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full text-white font-ibm-plex"
                style={{ backgroundColor: "var(--primary-color, #09090B)" }}
                disabled={loading}
              >
                {loading ? "Caricamento..." : "Accedi"}
              </Button>
              <a href="/dipendente" className="block text-center mt-3">
                <Button type="button" variant="outline" className="w-full font-ibm-plex text-sm" data-testid="login-employee-portal-btn">
                  Area Dipendenti
                </Button>
              </a>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
